import { sql } from "drizzle-orm"
import {
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { stateProvinceLists } from "./contact"
import { users } from "./users"

// Enums

export const userServiceStatusEnum = pgEnum("user_service_status_enum", [
  "PENDING",
  "PUBLISHED",
  "REJECTED",
])

export const userServiceTypeEnum = pgEnum("user_service_type_enum", [
  "APPRAISAL",
  "ATTORNEYS",
  "CHARITABLE_GIVING",
  "CPA",
  "ESTATE_SETTLEMENT",
  "FINANCIAL_ADVISOR",
  "FUNERAL_PLANNING",
  "INSURANCE",
  "ONLINE_NOTARIZATION",
  "ONLINE_WILL_AND_TRUST",
  "TRUST_COMPANY",
])

export const compensationModelEnum = pgEnum("compensation_model_enum", [
  "AFFILIATE_COMMISSION",
  "FLAT_LISTING",
  "NONE_REFERRAL_ONLY",
  "PER_LEAD",
  "REVENUE_SHARE",
])

export const integrationTypeEnum = pgEnum("integration_type_enum", [
  "AFFILIATE_LINK",
  "API_HANDOFF",
  "FORM_SUBMISSION",
  "MANUAL_INTRO",
])

export const userServiceAudienceEnum = pgEnum("user_service_audience_enum", [
  "INDIVIDUAL",
  "PROFESSIONAL",
])

export const connectionRequestOutcomeEnum = pgEnum("connection_request_outcome_enum", [
  "EMAIL_SENT",
  "REDIRECTED",
])

export const qualificationEnum = pgEnum("qualification_enum", [
  "NON_QUALIFIED_PARTNER",
  "PREFERRED_PARTNER",
  "QUALIFIED_PARTNER",
])

// Core Tables

// Purpose: Stores company information for the professional services directory.
// A single company can have multiple professional contacts listed.
export const userServiceCompanies = pgTable("user_service_company", {
  id: uuid("id").primaryKey().defaultRandom(),
  serviceCompanyName: varchar("service_company_name", { length: 255 }).notNull(),
  serviceCompanyDescription: text("service_company_description"),
  serviceCompanyLogoKey: varchar("service_company_logo_key", { length: 512 }),
  serviceCompanyUrl: varchar("service_company_url", { length: 512 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores individual professional listings in the services directory.
// Each listing links a professional contact to a company with directory-specific metadata.
// Email address is used for duplicate detection.
export const userServices = pgTable(
  "user_service",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userServiceCompanyId: uuid("user_service_company_id")
      .notNull()
      .references(() => userServiceCompanies.id, { onDelete: "cascade" }),
    serviceType: userServiceTypeEnum("service_type").notNull(),
    serviceContactFirstName: varchar("service_contact_first_name", { length: 100 }).notNull(),
    serviceContactLastName: varchar("service_contact_last_name", { length: 100 }).notNull(),
    serviceContactEmail: varchar("service_contact_email", { length: 255 }).notNull(),
    serviceContactPhone: varchar("service_contact_phone", { length: 50 }),
    serviceContactImageKey: varchar("service_contact_image_key", { length: 512 }),
    serviceContactUrl: varchar("service_contact_url", { length: 512 }),
    compensationModel: compensationModelEnum("compensation_model"),
    integrationType: integrationTypeEnum("integration_type"),
    qualification: qualificationEnum("qualification"),
    audience: userServiceAudienceEnum("audience").notNull().default("INDIVIDUAL"),
    affiliateLinkUrl: varchar("affiliate_link_url", { length: 512 }),
    status: userServiceStatusEnum("status").notNull().default("PENDING"),
    submittedAt: timestamp("submitted_at").defaultNow().notNull(),
    publishedAt: timestamp("published_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("user_service_audience_email_lower_uq").on(
      table.userServiceCompanyId,
      table.serviceType,
      sql`lower(${table.serviceContactEmail})`,
      table.audience,
    ),
  ],
)

// Junction Tables

// Purpose: Links a user service listing to the states/provinces it serves.
export const userServiceLocations = pgTable(
  "user_service_location",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userServiceId: uuid("user_service_id")
      .notNull()
      .references(() => userServices.id, { onDelete: "cascade" }),
    stateProvinceId: uuid("state_province_id")
      .notNull()
      .references(() => stateProvinceLists.id),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    unique("user_service_location_unique").on(table.userServiceId, table.stateProvinceId),
  ],
)

// Purpose: Tracks connection requests to professional service listings.
// On creation, an email is sent to Legacy Keeper admin for manual introduction.
// Listing references are nullable so request snapshots survive listing deletion.
// User deletion still cascades to remove the requester's personal data.
export const userServiceConnectionRequests = pgTable(
  "user_service_connection_request",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: varchar("user_id", { length: 255 }).references(() => users.id, {
      onDelete: "cascade",
    }),
    requesterName: varchar("requester_name", { length: 255 }).notNull(),
    requesterEmail: varchar("requester_email", { length: 255 }).notNull(),
    testatorName: varchar("testator_name", { length: 255 }),
    testatorEmail: varchar("testator_email", { length: 255 }),
    userServiceId: uuid("user_service_id").references(() => userServices.id, {
      onDelete: "set null",
    }),
    integrationType: integrationTypeEnum("integration_type"),
    outcome: connectionRequestOutcomeEnum("outcome"),
    serviceType: userServiceTypeEnum("service_type"),
    serviceCompanyName: varchar("service_company_name", { length: 255 }),
    serviceContactFirstName: varchar("service_contact_first_name", { length: 100 }),
    serviceContactLastName: varchar("service_contact_last_name", { length: 100 }),
    serviceContactEmail: varchar("service_contact_email", { length: 255 }),
    audience: userServiceAudienceEnum("audience"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [index("user_service_connection_request_user_id_idx").on(table.userId)],
)
